import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { Calendar, CaretDown, Plus } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import { Tag } from '@creditornot/cb-tag';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryParams } from 'use-query-params';

import { fetchPurchasesTotals } from 'apis/merchant-gateway/merchant-analytics/api';
import { isAnalyticsFilter } from 'apis/merchant-gateway/merchant-analytics/utils';
import PageHeader from 'common/components/PageHeader';
import { useRetailContext } from 'common/hooks';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { localize } from 'common/utils';
import { useVenueContext } from 'modules/venue/VenueContext';

import BarcodesModal from './BarcodesModal';
import DeliveryTypeSwitcher from './DeliveryTypeSwitcher';
import DownloadCSV from './DownloadCSV';
import FilterDateRangePicker, { isoDateToJsDate, Period } from './FilterDateRangePicker';
import FiltersContent from './FiltersContent';
import GetReceipts from './GetReceipts';
import PurchaseItemsTable from './PurchaseItemsTable';
import PurchaseOrders from './PurchaseOrders';
import PurchasesTableTotals from './PurchasesTableTotals';
import RootRedirector from './RootRedirector';
import { EARLIEST_SUPPORTED_DATETIME_FROM_NOW_IN_DAYS } from './constants';
import { purchasesQueryConfig, VIEW_TYPE } from './purchasesQueryConfig';
import {
  telemetryMerchantAdminPurchasesInteractionDownloadCsv,
  telemetryMerchantAdminPurchasesInteractionGetReceipts,
  telemetryMerchantAdminPurchasesInteractionSwitchToItems,
  telemetryMerchantAdminPurchasesInteractionSwitchToOrders,
  useTelemetryMerchantAdminPurchasesViewTracking,
} from './telemetry';
import { periodToIsoDateRange } from './utils';

const DEFAULT_PERIOD: Period = 'this-week';

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: flex-end;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    gap: 16px;
    flex-direction: column;
  }
`;

const HeaderButtons = styled.div`
  gap: 16px;
  display: flex;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    > * {
      flex-grow: 1;
    }
  }
`;

const DateRangePickerInput = styled(Input)`
  min-width: 230px;
`;

const DownloadButton = styled(Button)`
  @media (width <= ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const DownloadMenuContainer = styled.div`
  padding: 0 8px;
  min-width: 160px;
`;

const StyledGetReceipts = styled(GetReceipts)`
  width: 304px;
  padding: 8px;
`;

const StyledDownloadCSV = styled(DownloadCSV)`
  width: 352px;
  padding: 8px;
`;

const PurchasesContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

const SummaryHeader = styled.div`
  display: flex;
  padding: 16px 0 4px;
  flex-direction: row;
  align-items: center;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const Totals = styled(PurchasesTableTotals)`
  padding-inline: 24px 16px;
  border-inline-end: 1px solid ${cssThemedValue('borderPrimary')};

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    padding-inline: 24px;
    border-inline-end: none;
    border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
    padding-block-start: 16px;
    margin-block-start: 16px;
  }
`;

const Filters = styled.div`
  padding-inline: 16px 24px;
  display: flex;
  align-items: center;
  gap: 12px 16px;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    padding-inline: 24px;
  }
`;

const FilterPopover = styled(Popover)`
  @media (width <= ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const FilterButtonWrapper = styled.div`
  position: relative;
`;

// Hidden button to control the position of the filter popup to reduce jumping on filter count change
const FilterButtonWidthControl = styled(Button)`
  visibility: hidden;
`;

const FilterButton = styled(Button)`
  position: absolute;
  left: 0;
  top: 0;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 24px;
`;

const Purchases = () => {
  const intl = useIntl();
  const venue = useVenueContext();
  const { url: baseUrl } = useRouteMatch();
  const [now] = useState(Date.now());
  const history = useHistory();
  const [query, setQuery] = useQueryParams(purchasesQueryConfig);
  const [isBarCodesModalOpen, setIsBarCodesModalOpen] = useState(false);
  const isRetail = useRetailContext();
  useTelemetryMerchantAdminPurchasesViewTracking();

  const purchasesRouteMatch = useRouteMatch<{ period: string; filters: string }>(`${baseUrl}/:period`);

  const dateRange = purchasesRouteMatch
    ? periodToIsoDateRange(venue.timezone, now, purchasesRouteMatch.params.period)
    : undefined;

  const isDateRangeIntersectsWithAllowedRange = useMemo(() => {
    if (dateRange) {
      const fromDate = DateTime.fromJSDate(isoDateToJsDate(dateRange.from, venue.timezone));
      const minimumAllowedStartDate = DateTime.fromMillis(now).minus({
        days: EARLIEST_SUPPORTED_DATETIME_FROM_NOW_IN_DAYS,
      });
      return fromDate.startOf('day') < minimumAllowedStartDate.startOf('day');
    }
    return false;
  }, [dateRange, now, venue.timezone]);

  const generatePurchasesPeriodHref = useCallback(
    (period: string) => {
      return purchasesRouteMatch
        ? `${generatePath(purchasesRouteMatch.path, { ...purchasesRouteMatch.params, period })}${location.search}`
        : `${baseUrl}/${period}`;
    },
    [baseUrl, purchasesRouteMatch],
  );

  const handleDateRangeChange = useCallback(
    (period: string) => {
      const newUrl = generatePurchasesPeriodHref(period);
      history.push(newUrl);
    },
    [history, generatePurchasesPeriodHref],
  );

  useEffect(() => {
    if (purchasesRouteMatch?.params.period && !dateRange) {
      handleDateRangeChange(DEFAULT_PERIOD);
    }
  }, [dateRange, handleDateRangeChange, purchasesRouteMatch?.params.period]);

  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [downloadMenuContent, setDownloadMenuContent] = useState<'menu' | 'receipts' | 'csv'>('menu');

  const { data: purchasesTotals, isLoading } = useQuery(
    [`/merchant-analytics-api/v2/venues/${venue.id}/purchases/totals`, dateRange, query.filters],
    () => {
      if (!dateRange?.from || !dateRange.to || isDateRangeIntersectsWithAllowedRange) {
        return undefined;
      }

      return fetchPurchasesTotals({
        venueId: venue.id,
        startDate: dateRange.from,
        endDate: DateTime.fromISO(dateRange.to).plus({ days: 1 }).toISODate(),
        filters: query.filters?.split(',').filter(isAnalyticsFilter),
      });
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
    },
  );

  const handleChangeView = (value: VIEW_TYPE) => {
    setQuery({ view: value, filters: undefined, purchaseId: undefined, itemId: undefined });
  };

  const viewToggleOptions: { label: string; value: VIEW_TYPE }[] = [
    {
      label: `${intl.formatMessage({ id: 'purchases.tabs.orders' })} (${purchasesTotals?.count || 0})`,
      value: VIEW_TYPE.orders,
    },
    {
      label: intl.formatMessage({ id: 'purchases.tabs.items' }),
      value: VIEW_TYPE.items,
    },
  ];

  const filterCount = query.filters?.split(',').length;

  const [showFees, setShowFees] = useState(!!purchasesTotals?.count && purchasesTotals.feesDelivered.gross > 0);
  useEffect(() => {
    // Update showing fees logic only when there are purchases
    if (!isLoading && purchasesTotals?.count) {
      setShowFees(purchasesTotals.feesDelivered.gross > 0);
    }
  }, [purchasesTotals, isLoading]);

  return (
    <RootRedirector
      rootRoute={baseUrl}
      defaultSubRoute={`/${DEFAULT_PERIOD}`}
      localStorageKey="purchases.previousQuery"
    >
      <PageHeader title={intl.formatMessage({ id: 'navigation.purchases' })}>
        <HeaderActions>
          <Popover
            content={
              <FilterDateRangePicker
                onDateRangeChange={handleDateRangeChange}
                placeholder={intl.formatMessage({ id: 'purchases.choose-dates' })}
                value={dateRange}
                timezone={venue.timezone}
              />
            }
            showOnClick
            showOnHover={false}
            renderArrow={false}
            placement="bottom"
          >
            <DateRangePickerInput
              value={`${
                dateRange?.from
                  ? DateTime.fromFormat(dateRange.from, 'yyyy-MM-dd', { zone: venue.timezone }).toLocaleString(
                      DateTime.DATE_SHORT,
                      {
                        locale: intl.locale,
                      },
                    )
                  : ''
              } - ${
                dateRange?.to
                  ? DateTime.fromFormat(dateRange.to, 'yyyy-MM-dd', { zone: venue.timezone }).toLocaleString(
                      DateTime.DATE_SHORT,
                      {
                        locale: intl.locale,
                      },
                    )
                  : ''
              }`}
              icon={<Calendar />}
              readOnly
            />
          </Popover>

          <HeaderButtons>
            <DeliveryTypeSwitcher />

            <Popover
              placement="bottom-end"
              onClickOutside={() => setIsDownloadMenuOpen(false)}
              show={isDownloadMenuOpen}
              showOnHover={false}
              renderArrow={false}
              content={
                downloadMenuContent === 'receipts' ? (
                  <StyledGetReceipts
                    initialDateRange={dateRange}
                    onClose={() => setIsDownloadMenuOpen(false)}
                    venueId={venue.id}
                  />
                ) : downloadMenuContent === 'csv' && dateRange ? (
                  <StyledDownloadCSV
                    venueId={venue.id}
                    timezone={venue.timezone}
                    isDownloadingDisabled={isDateRangeIntersectsWithAllowedRange || query.filters === 'active'}
                    dateRange={dateRange}
                    query={query}
                    onClose={() => setIsDownloadMenuOpen(false)}
                  />
                ) : (
                  <DownloadMenuContainer>
                    <MenuItemGroup>
                      {dateRange && (
                        <MenuActionItem
                          onClick={() => {
                            telemetryMerchantAdminPurchasesInteractionDownloadCsv();
                            setDownloadMenuContent('csv');
                          }}
                          variant="black"
                        >
                          <FormattedMessage id="purchases.download-csv" />
                        </MenuActionItem>
                      )}
                      <MenuActionItem
                        variant="black"
                        onClick={() => {
                          telemetryMerchantAdminPurchasesInteractionGetReceipts();
                          setDownloadMenuContent('receipts');
                        }}
                      >
                        <FormattedMessage id="purchases.getReceipts" />
                      </MenuActionItem>
                      {isRetail && (
                        <MenuActionItem
                          variant="black"
                          onClick={() => {
                            setIsDownloadMenuOpen(false);
                            setIsBarCodesModalOpen(true);
                          }}
                        >
                          <FormattedMessage id="purchases.barcodes.action" />
                        </MenuActionItem>
                      )}
                    </MenuItemGroup>
                  </DownloadMenuContainer>
                )
              }
            >
              <DownloadButton
                onClick={() => {
                  if (!isDownloadMenuOpen) {
                    setDownloadMenuContent('menu');
                    setIsDownloadMenuOpen(true);
                  } else if (downloadMenuContent === 'menu') {
                    setIsDownloadMenuOpen(false);
                  } else {
                    setDownloadMenuContent('menu');
                  }
                }}
                icon={<CaretDown />}
                iconPosition="right"
                size="small"
                variant="blue"
              >
                <FormattedMessage id="state.download" />
              </DownloadButton>
            </Popover>
          </HeaderButtons>
        </HeaderActions>
      </PageHeader>
      <PurchasesContainer>
        <SummaryHeader>
          <Totals
            purchasesTotals={purchasesTotals}
            showFees={showFees}
            currency={venue.currency}
            isLoading={isLoading}
          />

          <Filters>
            <SegmentedControl
              // when label is dynamically changed (e.g. count, or language change), component layout gets broken.
              // we use key to force re-render and fix the layout.
              key={viewToggleOptions.map(option => option.label).join()}
              onChange={value => {
                if (value === VIEW_TYPE.orders) {
                  telemetryMerchantAdminPurchasesInteractionSwitchToOrders();
                } else if (value === VIEW_TYPE.items) {
                  telemetryMerchantAdminPurchasesInteractionSwitchToItems();
                }
                handleChangeView(value);
              }}
              options={viewToggleOptions}
              value={query.view}
              disabled={isDateRangeIntersectsWithAllowedRange}
            />

            <FilterPopover
              content={({ close }) => <FiltersContent setQuery={setQuery} query={query} onClose={close} />}
              disableContentPadding
              showOnClick
              showOnHover={false}
              renderArrow={false}
              placement="bottom"
            >
              <FilterButtonWrapper>
                <FilterButtonWidthControl
                  variant="lightBlue"
                  size="small"
                  icon={
                    <Tag variant="blue" size="tiny">
                      10
                    </Tag>
                  }
                  tabIndex={-1}
                >
                  <FormattedMessage id="purchases.labels.filter" />
                </FilterButtonWidthControl>
                <FilterButton
                  variant="lightBlue"
                  size="small"
                  icon={
                    !filterCount ? (
                      <Plus />
                    ) : (
                      <Tag variant="blue" size="tiny">
                        {filterCount}
                      </Tag>
                    )
                  }
                  iconPosition="right"
                >
                  <FormattedMessage id="purchases.labels.filter" />
                </FilterButton>
              </FilterButtonWrapper>
            </FilterPopover>
          </Filters>
        </SummaryHeader>

        {!isDateRangeIntersectsWithAllowedRange && (
          <>
            {dateRange && query.view === VIEW_TYPE.orders && (
              <PurchaseOrders dateRange={dateRange} query={query} setQuery={setQuery} showFees={showFees} />
            )}
            {dateRange && query.view === VIEW_TYPE.items && (
              <PurchaseItemsTable dateRange={dateRange} query={query} setQuery={setQuery} />
            )}
          </>
        )}

        {isDateRangeIntersectsWithAllowedRange && (
          <ErrorAlert variant="error">
            <FormattedMessage id="purchase-history.date-out-of-range" />
          </ErrorAlert>
        )}
        {dateRange && isBarCodesModalOpen && (
          <BarcodesModal
            range={{
              from: DateTime.fromISO(dateRange.from, { zone: venue.timezone }).toISO(),
              to: DateTime.fromISO(dateRange.to, { zone: venue.timezone }).plus({ days: 1 }).toISO(),
            }}
            venueId={venue.id}
            venueName={localize(venue.name, intl.locale)}
            onClose={() => setIsBarCodesModalOpen(false)}
          />
        )}
      </PurchasesContainer>
    </RootRedirector>
  );
};

export default Purchases;
